import { CommonModule } from '@angular/common';
import { Component, DestroyRef, computed, effect, inject, input, signal, untracked } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { finalize } from 'rxjs/operators';
import { Select } from 'primeng/select';
import { PaginatorState } from 'primeng/paginator';
import { Button } from 'primeng/button';
import { Dialog } from 'primeng/dialog';
import { InputText } from 'primeng/inputtext';

import { PessoaDomain } from '@/core/api/sicom-api/registro/pessoa/pessoa.domain';
import { PrestadorComissaoCargoResponse, PrestadorComissaoComposicaoFiltro, PrestadorComissaoMembroResponse, PrestadorComissaoMembroSalvarRequest, PrestadorComissaoTipo } from '@/core/api/sicom-api/registro/prestador/prestador-comissoes.model';
import { PrestadorComissoesService } from '@/core/api/sicom-api/registro/prestador/prestador-comissoes.service';
import { CardContent } from '@/shared/components/card-content/card-content';
import { CustomTableComponent, TableMetadata, TablePerfilValue } from '@/shared/components/layout/custom-table/custom-table.component';
import { FotoMedicoCacheService } from '@/shared/components/foto-diretor/foto-medico-cache.service';
import { DateUtils } from '@/shared/utils/date-utils';
import { TabContainer, TabContainerOptionType, TabContent } from '@/shared/components/layout/tab-container/tab-container';
import { SeletorMedicoCorpoClinico } from '@/shared/components/pessoa/seletor-medico-corpo-clinico/seletor-medico-corpo-clinico';
import { COMISSOES_FINANCEIRO_RETURN_CONTEXT_ID, PrestadorFinanceiroNavigationService } from '@/features/registro/prestadores/dashboard-prestadores/components/prestador-financeiro-navigation.service';
import { CanDirective } from '@/core/permissions/can.directive';
import { PERMISSOES } from '@/core/permissions/permissoes.catalog';

import { DialogoPadraoDirective } from '@/shared/components/layout/dialogo-padrao/dialogo-padrao.directive';
type OpcaoComposicao = {
    label: string;
    value: PrestadorComissaoComposicaoFiltro;
};

type TableTag = {
    value: string;
    color?: string;
    textColor?: string;
};

type ComissaoTabelaRow = {
    id: string;
    uf: string;
    crm: string;
    nome: TablePerfilValue;
    cargo: string;
    entrada: string;
    saida: string;
    prazoRestante: string;
    situacao: string;
    alerta: TableTag;
    source: PrestadorComissaoMembroResponse;
};

@Component({
    selector: 'app-comissao-etica-prestador',
    standalone: true,
    imports: [DialogoPadraoDirective, CommonModule, FormsModule, Button, CanDirective, CardContent, CustomTableComponent, Dialog, InputText, SeletorMedicoCorpoClinico, Select, TabContainer, TabContent],
    templateUrl: './comissao-etica-prestador.html',
    styleUrl: './comissao-etica-prestador.scss'
})
export class ComissaoEticaPrestador {
    pessoa = input<PessoaDomain>();

    readonly carregando = signal(false);
    readonly cargos = signal<PrestadorComissaoCargoResponse[]>([]);
    readonly membros = signal<PrestadorComissaoMembroResponse[]>([]);
    readonly membrosTabela = computed<ComissaoTabelaRow[]>(() => this.membros().map((membro) => this.toTabelaRow(membro)));
    readonly totalRecords = signal(0);
    readonly first = signal(0);
    readonly rows = signal(10);
    readonly dialogVisivel = signal(false);
    readonly pesquisaMedicoDialog = signal(false);
    readonly salvando = signal(false);
    readonly membroEmEdicao = signal<PrestadorComissaoMembroResponse | null>(null);

    selected: PrestadorComissaoTipo = 'ETICA';
    composicao: PrestadorComissaoComposicaoFiltro = 'ATUAL';
    formulario: {
        idMembro: number | null;
        ufMembro: string;
        crm: string;
        nome: string;
        idCargo: number | null;
        entrada: string;
        saida: string;
    } = this.novoFormulario();
    readonly options: TabContainerOptionType[] = [
        { label: 'Ética', value: 'ETICA' },
        { label: 'Revisão de Prontuário', value: 'REVISAO_PRONTUARIO' },
        { label: 'Revisão de Óbito', value: 'REVISAO_OBITO' },
        { label: 'Controle de Infecção', value: 'CONTROLE_INFECCAO' },
        { label: 'PGRSS', value: 'PGRSS' },
        { label: 'NSP', value: 'NSP' }
    ];
    readonly composicoes: OpcaoComposicao[] = [
        { label: 'Composição Atual', value: 'ATUAL' },
        { label: 'Composição Antiga', value: 'ANTIGA' }
    ];
    readonly columnOrder = ['uf', 'crm', 'nome', 'cargo', 'entrada', 'saida', 'prazoRestante', 'situacao', 'alerta'];
    protected readonly roleEditar = PERMISSOES.prestadores.comissaoEtica.editar;
    readonly tableMetadata: TableMetadata = {
        headers: {
            uf: { type: 'text', default: '-', hidden: false, value: 'UF' },
            crm: { type: 'text', default: '-', hidden: false, value: 'CRM' },
            nome: { type: 'perfil', default: '-', hidden: false, value: 'Nome' },
            cargo: { type: 'text', default: '-', hidden: false, value: 'Cargo' },
            entrada: { type: 'text', default: '-', hidden: false, value: 'Entrada' },
            saida: { type: 'text', default: '-', hidden: false, value: 'Saída' },
            prazoRestante: { type: 'text', default: '-', hidden: false, value: 'Vence em' },
            situacao: { type: 'text', default: '-', hidden: false, value: 'Situação' },
            alerta: { type: 'tag', default: '-', hidden: false, value: 'Alertas' }
        },
        title: 'nome',
        actions: {
            editar: {
                label: 'Editar',
                icon: 'pi pi-pencil',
                color: 'secondary',
                command: (item: ComissaoTabelaRow) => this.editarMembro(item.source),
                permission: this.roleEditar
            },
            remover: {
                label: 'Remover',
                icon: 'pi pi-trash',
                color: 'danger',
                command: (item: ComissaoTabelaRow) => this.removerMembro(item.source),
                permission: this.roleEditar
            },
            informacoesFinanceiras: {
                label: 'Informações financeiras',
                icon: 'pi pi-dollar',
                color: 'success',
                command: (item: ComissaoTabelaRow) => this.abrirInformacoesFinanceiras(item.source)
            }
        },
        defaultSortField: 'nome',
        defaultSortOrder: 1
    };

    private readonly destroyRef = inject(DestroyRef);
    private readonly fotos = inject(FotoMedicoCacheService);
    private readonly prestadorComissoesService = inject(PrestadorComissoesService);
    private readonly financeiroNavigationService = inject(PrestadorFinanceiroNavigationService);

    constructor() {
        this.carregarCargos();
        effect(() => {
            const pessoa = this.pessoa();
            console.debug('[prestadores-return] comissoes pessoa input', this.resumoPessoa(pessoa));
            if (!pessoa?.id || !pessoa.uf) {
                console.debug('[prestadores-return] comissoes limpar por pessoa invalida', this.resumoPessoa(pessoa));
                this.limpar();
                return;
            }

            untracked(() => this.carregar());
        });
    }

    private carregarCargos(): void {
        this.prestadorComissoesService
            .listarCargos()
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((cargos) => this.cargos.set(cargos));
    }

    onTipoComissaoAlterado(event: { value: string }): void {
        this.selected = event.value as PrestadorComissaoTipo;
        this.first.set(0);
        this.carregar();
    }

    onComposicaoAlterada(): void {
        this.first.set(0);
        this.carregar();
    }

    onPaginaAlterada(event: PaginatorState): void {
        this.first.set(event.first ?? 0);
        this.rows.set(event.rows ?? 10);
        this.carregar();
    }

    novoMembro(): void {
        this.membroEmEdicao.set(null);
        this.formulario = this.novoFormulario();
        this.dialogVisivel.set(true);
    }

    editarMembro(membro: PrestadorComissaoMembroResponse): void {
        this.membroEmEdicao.set(membro);
        this.formulario = {
            idMembro: membro.idMembro,
            ufMembro: membro.uf,
            crm: membro.crm ?? '',
            nome: membro.nome ?? '',
            idCargo: membro.idCargo ?? null,
            entrada: membro.entrada ?? '',
            saida: membro.saida ?? ''
        };
        this.dialogVisivel.set(true);
    }

    abrirPesquisaMedico(): void {
        this.pesquisaMedicoDialog.set(true);
    }

    selecionarMedico(medico: PessoaDomain): void {
        this.formulario.idMembro = Number(medico.id);
        this.formulario.ufMembro = medico.uf;
        this.formulario.crm = medico.crm ?? '';
        this.formulario.nome = medico.nome ?? '';
    }

    salvarMembro(): void {
        const pessoa = this.pessoa();
        if (!pessoa?.id || !pessoa.uf || !this.formulario.idMembro || !this.formulario.ufMembro || !this.formulario.entrada) {
            return;
        }

        const request: PrestadorComissaoMembroSalvarRequest = {
            id: Number(pessoa.id),
            uf: pessoa.uf,
            tipoComissao: this.selected,
            idMembro: this.formulario.idMembro,
            ufMembro: this.formulario.ufMembro,
            idCargo: this.formulario.idCargo,
            entrada: this.formulario.entrada,
            saida: this.formulario.saida || null
        };
        const membroOriginal = this.membroEmEdicao();
        const requisicao = membroOriginal
            ? this.prestadorComissoesService.atualizar({
                  ...request,
                  idMembroOriginal: membroOriginal.idMembro,
                  ufMembroOriginal: membroOriginal.uf,
                  entradaOriginal: membroOriginal.entrada ?? request.entrada
              })
            : this.prestadorComissoesService.criar(request);

        this.salvando.set(true);
        // Recusa nao desfaz nada aqui de proposito: o formulario/dialogo fica como esta para o usuario corrigir, e o toast vem do interceptor global.
        requisicao.pipe(finalize(() => this.salvando.set(false))).subscribe(() => {
            this.dialogVisivel.set(false);
            this.first.set(0);
            this.carregar();
        });
    }

    removerMembro(membro: PrestadorComissaoMembroResponse): void {
        const pessoa = this.pessoa();
        if (!pessoa?.id || !pessoa.uf || !membro.entrada || !confirm(`Remover ${membro.nome ?? 'membro'} da comissão?`)) {
            return;
        }

        this.prestadorComissoesService
            .remover({
                id: Number(pessoa.id),
                uf: pessoa.uf,
                tipoComissao: this.selected,
                idMembro: membro.idMembro,
                ufMembro: membro.uf,
                entrada: membro.entrada
            })
            // Recusa nao desfaz nada aqui de proposito: o formulario/dialogo fica como esta para o usuario corrigir, e o toast vem do interceptor global.
            .subscribe(() => {
                this.first.set(0);
                this.carregar();
            });
    }

    abrirInformacoesFinanceiras(membro: PrestadorComissaoMembroResponse): void {
        this.financeiroNavigationService.abrirMedico(
            this.pessoa(),
            {
                id: membro.idMembro,
                uf: membro.uf,
                crm: membro.crm ?? undefined,
                nome: membro.nome ?? undefined,
                tipo: 'MEDICO'
            },
            {
                id: COMISSOES_FINANCEIRO_RETURN_CONTEXT_ID,
                returnUrl: '/prestadores/comissao-etica'
            }
        );
    }

    private carregar(): void {
        const pessoa = this.pessoa();
        console.debug('[prestadores-return] comissoes carregar trigger', {
            pessoa: this.resumoPessoa(pessoa),
            tipoComissao: this.selected,
            composicao: this.composicao,
            first: this.first(),
            rows: this.rows()
        });
        if (!pessoa?.id || !pessoa.uf) {
            this.limpar();
            return;
        }

        this.carregando.set(true);
        this.prestadorComissoesService
            .listar({
                id: Number(pessoa.id),
                uf: pessoa.uf,
                tipoComissao: this.selected,
                composicao: this.composicao,
                sortBy: 'NOME',
                direction: 'ASC',
                page: Math.floor(this.first() / this.rows()),
                size: this.rows()
            })
            .pipe(
                takeUntilDestroyed(this.destroyRef),
                finalize(() => this.carregando.set(false))
            )
            .subscribe((response) => {
                console.debug('[prestadores-return] comissoes carregar response', {
                    totalElements: response.totalElements,
                    contentLength: response.content?.length ?? 0
                });
                const membros = response.content ?? [];
                this.membros.set(membros);
                // As linhas são `computed` sobre o cache: cada retrato que chega refaz a sua linha.
                this.fotos.precarregar(membros.map((membro) => ({ crm: membro.crm, uf: membro.uf })));
                this.totalRecords.set(response.totalElements ?? 0);
            });
    }

    private limpar(): void {
        this.membros.set([]);
        this.totalRecords.set(0);
        this.first.set(0);
    }

    private novoFormulario() {
        return { idMembro: null, ufMembro: '', crm: '', nome: '', idCargo: null, entrada: '', saida: '' };
    }

    private resumoPessoa(pessoa: PessoaDomain | null | undefined) {
        if (!pessoa) {
            return null;
        }
        return {
            id: pessoa.id,
            uf: pessoa.uf,
            tipo: pessoa.tipo,
            crm: pessoa.crm,
            nome: pessoa.nome
        };
    }

    private toTabelaRow(membro: PrestadorComissaoMembroResponse): ComissaoTabelaRow {
        return {
            id: `${membro.uf}-${membro.idMembro}`,
            uf: membro.uf,
            crm: membro.crm ?? '-',
            nome: this.fotos.perfilMedico(membro.nome, membro.crm, membro.uf),
            cargo: membro.cargo ?? '-',
            entrada: this.formatarData(membro.entrada),
            saida: this.formatarData(membro.saida),
            prazoRestante: membro.prazoRestante ?? '-',
            situacao: membro.situacao ?? '-',
            alerta: this.alerta(membro),
            source: membro
        };
    }

    private formatarData(data?: string | null): string {
        return data ? DateUtils.formatDate(data) : '-';
    }

    private alerta(membro: PrestadorComissaoMembroResponse): TableTag {
        if (membro.situacao?.toLowerCase() === 'falecido') {
            return { value: 'Falecido', color: '#ef4444', textColor: '#ffffff' };
        }
        if (membro.possuiDebitosAbertos) {
            return { value: 'Devedor', color: '#f59e0b', textColor: '#111827' };
        }
        return { value: 'Sem alerta', color: '#10b981', textColor: '#ffffff' };
    }
}
